import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";

// ---- Types ----
export interface Exhibition {
  id: number
  title: string
}

export interface ExhibitionStandCategory {
  id: number
  name: string
  category: number // ID of the stand category
  exhibition: number
  stand_name?: string // Optional field to hold stand name
}

export interface Participation {
  id: number
  exhibition: number
  exhibitor: number
  stand_category: number
  status: boolean
  exhibition_title?: string
  exhibitor_name?: string
  num_of_stands: number
  stand_category_name?: string
  created_by?: string
  created_at?: string
}

interface Representation {
  id: number
  person_id: number
  person_full_name: string
  person_email: string
  person_phone: string
  person_id_number: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  stand_category: string
  profile_photo_url: string | null
  has_profile_photo: boolean
  total_badges_printed: number
  has_original_badge: boolean
  last_badge_type: string | null
  last_printed_at: string | null
  can_print_original: boolean
  status: boolean
  created_at: string
}

export interface BadgeRequestData {
  representation_id: number;
  print_order_id: number;
  print_type: "original" | "copy";
  force_generation?: boolean;
  template_name?: string;
  confirmed_fee: number;
}

export interface BadgeResponse {
  success: boolean;
  badge_data: any;
  template_config: any;
  pricing?: {
    payment_required: boolean;
    fee: number;
    currency: string;
  };
  badge_tracking_id?: number;
  message?: string;
  confirmed_fee: 0;
}

export interface BadgeHistoryItem {
  id: number;
  person_name: string;
  exhibitor_name: string;
  exhibition_title: string;
  print_type: string;
  print_sequence: number;
  verification_code: string;
  fee_charged: number;
  payment_required: boolean;
  payment_status: string;
  design_template: string;
  printed_by_name: string;
  printed_at: string;
  is_active: boolean;
}

export interface ExhibitionPricing {
  id: number
  exhibition: number
  badge_fee: number
  badge_reprint_price: number
  visitor_entry_fee: number
  parking_fee_per_minute: number
  currency: string
    created_by?: string
    updated_by?: string
 
}

// Focal Person Creation Interface
export interface CreateFocalPersonRequest {
  first_name: string
  middle_name?: string
  last_name: string
  email: string
  phone?: string
  exhibitor_id: number
}

export interface FocalPersonResponse {
  success: boolean
  message: string
  person_id: number
  account_id: number
  verification_sent: boolean
}

export interface FocalPersonMatch {
  person_id: number
  account_id: number | null
  first_name: string
  middle_name?: string | null
  last_name: string
  email: string
  phone: string
  email_verified: boolean
  account_status: string | null
  last_exhibition: string | null
  last_exhibition_id: number | null
  was_focal_person: boolean
  last_role: string
}

export interface CurrentFocalPerson {
  person_id: number
  account_id: number | null
  first_name: string
  middle_name?: string | null
  last_name: string
  email: string
  phone: string
  email_verified: boolean
  account_status: string | null
}

export interface SearchFocalPersonResponse {
  success: boolean
  exhibitor_id: number
  has_current_focal_person: boolean
  current_focal_persons: CurrentFocalPerson[]
  matches: FocalPersonMatch[]
  count: number
}

export interface ConfirmFocalPersonResponse {
  success: boolean
  message: string
  person_id: number
  account_id: number
  exhibitor_id: number
  participation_id: number
  representation_id: number
  account_created?: boolean
  email_sent_to: string[]
}

interface SendFocalPersonEmailResponse {
  success: boolean
  message: string
  exhibitor_id: number
  email_sent_to: string
  token_expires_in_days: number
}

// Generic paginated response
interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

// ---- Axios setup ----
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: { "Content-Type": "application/json" },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken")
  if (token) config.headers!["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
  return config
})

// ---- Services ----
export const ExhibitorRelatedService = {
  // 1️⃣ Get list of all exhibitions for the dropdown
  getExhibitions: async (): Promise<Exhibition[]> => {
    const resp = await api.get("/api/exhibitions/get-exhibitions/")
    return resp.data
  },

  // 2️⃣ Get stand categories for a given exhibition
  getStandCategories: async (
    exhibitionId: number
  ): Promise<ExhibitionStandCategory[]> => {
    const resp = await api.get( `/api/exhibitions/get-exhibition-stand-categories/${exhibitionId}/`)
    return resp.data
  },

  getParticipationsByExhibitor: async (
    exhibitorId: number
  ): Promise<PaginatedResponse<Participation>> => {
    const resp = await api.get<PaginatedResponse<Participation>>(
      `/api/stakeholders/get-participations-by-exhibitor/${exhibitorId}/`
    )
    return resp.data
  },

   getExhibitionPricing: async (exhibitionId: number): Promise<ExhibitionPricing> => {
      const response = await api.get(`/api/exhibitions/pricing-by-exhibition/${exhibitionId}/`)
      return response.data
    },

   getRepresentationsByExhibitor: async (
    exhibitorId: number,
    page: number = 1,
    perPage: number = 10
  ): Promise<PaginatedResponse<Representation>> => {
    const resp = await api.get<PaginatedResponse<Representation>>(
      `/api/stakeholders/get-representations-by-exhibitor/${exhibitorId}/`, {
      params: {
        page,
        per_page: perPage
      }
    }
    )
    return resp.data
  },




  // 3️⃣ Participation CRUD
  getParticipation: async (id: number): Promise<Participation> => {
    const resp = await api.get(`/api/stakeholders/participations/${id}/`)
    return resp.data
  },

  createParticipation: async (
    data: Omit<Participation, "id">
  ): Promise<Participation> => {
    const resp = await api.post("/api/stakeholders/participations/create/", data)
    return resp.data
  },
  updateParticipation: async (
    id: number,
    data: Omit<Participation, "id">
  ): Promise<Participation> => {
    const resp = await api.put(`/api/stakeholders/participations/${id}/update/`, data)
    return resp.data
  },
  deleteParticipation: async (id: number): Promise<void> => {
    await api.delete(`/api/stakeholders/participations/${id}/delete/`)
  },

   async requestBadge(data: BadgeRequestData): Promise<BadgeResponse> {
    const response = await api.post("/api/badges/badge/request/", data);
    return response.data;
  }
,
  // Get badge history for a person
  async getBadgeHistory(personId: number): Promise<{ success: boolean; badge_history: BadgeHistoryItem[] }> {
    const response = await api.get(`/api/badges/person/${personId}/badge-history/`);
    return response.data;
  },

  // Confirm payment for a badge
  async confirmPayment(badgeTrackingId: number): Promise<{ success: boolean }> {
    const response = await api.post(`/api/badges/badge/${badgeTrackingId}/payment/`, {
      payment_status: "paid"
    });
    return response.data;
  },

  async createFocalPerson(data: CreateFocalPersonRequest): Promise<FocalPersonResponse> {
    const response = await api.post("/api/stakeholders/create-focal-person/", data)
    return response.data
  },

  // Search for an existing focal person tied to this exhibitor from a
  // previous exhibition participation, so we can reuse/confirm them instead
  // of creating a duplicate account.
  async searchFocalPerson(exhibitorId: number): Promise<SearchFocalPersonResponse> {
    const response = await api.get(`/api/stakeholders/exhibitors/${exhibitorId}/search-focal-person/`)
    return response.data
  },

  // Confirm/reuse an existing focal person for the exhibitor's current
  // active participation.
  async confirmFocalPerson(exhibitorId: number, personId: number): Promise<ConfirmFocalPersonResponse> {
    const response = await api.post("/api/stakeholders/confirm-focal-person/", {
      exhibitor_id: exhibitorId,
      person_id: personId,
    })
    return response.data
  },

 async sendFocalPersonIdentificationEmail(exhibitorId: number): Promise<SendFocalPersonEmailResponse> {
    const response = await api.post(`/api/stakeholders/exhibitors/${exhibitorId}/send-focal-person-email/`)
    return response.data
  }
}
